<!--
  Copyright (C) 2022 - Jeci SARL - https://jeci.fr

  This program is free software: you can redistribute it and/or modify
  it under the terms of the GNU Affero General Public License as
  published by the Free Software Foundation, either version 3 of the
  License, or (at your option) any later version.

  This program is distributed in the hope that it will be useful,
  but WITHOUT ANY WARRANTY; without even the implied warranty of
  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
  GNU Affero General Public License for more details.

  You should have received a copy of the GNU Affero General Public License
  along with this program.  If not, see https://www.gnu.org/licenses/agpl-3.0.html.
-->

<template>
  <div class="external-apps-grid">
    <div
      v-for="item in mappedItems"
      :key="item.type"
      class="external-app-card"
      @click="handleClick(item)"
    >
      <template v-if="useFavicon">
        <img
          :src="getFaviconUrl(item.link)"
          alt="favicon"
          class="external-app-favicon"
          width="32"
          height="32"
        />
      </template>
      <template v-else>
        <i
          :class="item.customIcon || item.icon"
          class="external-app-icon"
          :style="{
            color: item.color ? `#${item.color}` : 'var(--p-text-color)',
          }"
        ></i>
      </template>
      <span class="external-app-label">{{ item.label }}</span>
    </div>
  </div>
</template>

<script>
export default {
  name: "ExternalApps",
  props: {
    config: {
      type: Array,
      required: true,
      default: () => [],
    },
    useFavicon: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    mappedItems() {
      const typeMap = {
        calendar: { label: "Calendar", icon: "ri-calendar-line" },
        chat: { label: "Chat", icon: "ri-chat-3-line" },
        contacts: { label: "Contacts", icon: "ri-contacts-line" },
        docs: { label: "Docs", icon: "ri-file-text-line" },
        email: { label: "Email", icon: "ri-mail-line" },
        storage: { label: "File storage", icon: "ri-folder-line" },
        iam: { label: "IAM", icon: "ri-home-5-line" },
        notes: { label: "Notes", icon: "ri-edit-line" },
        projects: { label: "Projects", icon: "ri-focus-2-line" },
        tasks: { label: "Tasks", icon: "ri-list-check-2" },
        video: { label: "Video calls", icon: "ri-video-chat-line" },
        wiki: { label: "Wiki", icon: "ri-lightbulb-line" },
        other: { label: "Other", icon: "ri-question-line" },
      };

      return this.config.map((item) => {
        const mapping = typeMap[item.type] || {
          label: item.type,
          icon: "ri-question-line",
        };
        return {
          ...item,
          label: item.value || mapping.label,
          icon: mapping.icon,
          customIcon: item.icon, // User-selected icon
          color: item.color, // User-selected color
        };
      });
    },
  },
  methods: {
    handleClick(item) {
      window.open(item.link, "_blank");
    },
    getFaviconUrl(link) {
      const url = new URL(
        link.startsWith("http") ? link : window.location.origin + link,
      );
      return `${url.origin}/favicon.ico`;
    },
  },
};
</script>

<style scoped>
.external-apps-grid {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 0.75rem;
}

.external-app-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  border-radius: 0.5rem;
  padding: 0.5rem;
  background: var(--p-content-background);
  border: 1px solid var(--p-content-border-color);
  cursor: pointer;
  transition: transform 0.2s;
  width: 7rem;
  height: 7rem;
}

.external-app-card:hover {
  background: var(--p-highlight-background);
}

.external-app-favicon {
  margin-bottom: 0.5rem;
  object-fit: contain;
}

.external-app-icon {
  font-size: 1.75rem;
  margin-bottom: 0.5rem;
}

.external-app-label {
  font-size: 1rem;
  text-align: center;
}
</style>
